import { logPings, type PingInput } from '../api/rpc';
import { config } from '../config';
import { Emitter } from './emitter';

export interface Fix {
  lat: number;
  lon: number;
  /** 精度（m） */
  acc: number;
  /** 端末で取れた時刻（ミリ秒） */
  at: number;
}

/** ok＝取れている / poor＝精度50m超（サーバーの area_check_max_acc_m と同じ） / none＝取れていない / denied＝拒否 */
export type GeoStatus = 'ok' | 'poor' | 'none' | 'denied' | 'unknown';

export const POOR_ACC_M = 50;
/** これより古い位置は提出に使わない（サーバーへ古い位置を送らない＝012の方針） */
const FRESH_MS = 60_000;
/** 位置の記録は30秒に1点まで、60秒ごとにまとめて送る */
const PING_EVERY_MS = 30_000;
const FLUSH_EVERY_MS = 60_000;

class Geo {
  readonly changed = new Emitter<{ status: GeoStatus; fix: Fix | null }>();
  private last: Fix | null = null;
  private st: GeoStatus = 'unknown';
  private watchId: number | null = null;
  private buf: PingInput[] = [];
  private seq = 0;
  private lastPingAt = 0;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private override: Fix | null = null;

  get status(): GeoStatus {
    return this.st;
  }

  /** 表示用の最後の位置（古くても返す。取れていないときの半透明の蜂に使う） */
  get lastFix(): Fix | null {
    return this.override ?? this.last;
  }

  /** 提出・記録に使う位置。古い位置は返さない */
  fresh(): Fix | null {
    const f = this.lastFix;
    return f && Date.now() - f.at <= FRESH_MS ? f : null;
  }

  async permission(): Promise<PermissionState | 'unsupported'> {
    if (!('geolocation' in navigator)) return 'unsupported';
    try {
      const p = await navigator.permissions.query({ name: 'geolocation' });
      return p.state;
    } catch {
      return 'prompt';
    }
  }

  /** ブラウザの確認を出して監視を始める。拒否なら 'denied' を返す */
  start(): Promise<GeoStatus> {
    return new Promise((resolve) => {
      if (this.watchId !== null) return resolve(this.st);
      if (!('geolocation' in navigator)) {
        this.set('none');
        return resolve('none');
      }
      let first = true;
      this.watchId = navigator.geolocation.watchPosition(
        (p) => {
          this.onFix({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy, at: p.timestamp || Date.now() });
          if (first) {
            first = false;
            resolve(this.st);
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            this.set('denied');
            this.stop();
          } else this.set('none');
          // 拒否でも欠測を偏らせないよう、位置なしの記録を残す（合宿版の対策）
          this.ping(null);
          if (first) {
            first = false;
            resolve(this.st);
          }
        },
        { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
      );
      this.flushTimer ??= setInterval(() => void this.flush(), FLUSH_EVERY_MS);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.installDevOverride();
    });
  }

  stop(): void {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }

  private onFix(f: Fix): void {
    if (this.override) return;
    this.last = f;
    this.set(f.acc > POOR_ACC_M ? 'poor' : 'ok');
    this.ping(f);
  }

  private set(s: GeoStatus): void {
    this.st = s;
    this.changed.emit({ status: s, fix: this.lastFix });
  }

  private ping(f: Fix | null, force = false): void {
    const now = Date.now();
    if (!force && now - this.lastPingAt < PING_EVERY_MS) return;
    this.lastPingAt = now;
    this.buf.push({
      seq: ++this.seq,
      client_at: new Date(f?.at ?? now).toISOString(),
      lat: f?.lat ?? null,
      lon: f?.lon ?? null,
      acc: f?.acc ?? null,
      visibility: document.visibilityState,
      online: navigator.onLine,
    });
    if (this.buf.length > 500) this.buf.splice(0, this.buf.length - 500);
  }

  private onVisibility = (): void => {
    this.ping(this.fresh(), true);
    if (document.visibilityState === 'hidden') void this.flush();
  };

  async flush(): Promise<void> {
    if (!this.buf.length || !navigator.onLine) return;
    const batch = this.buf.splice(0, 200);
    try {
      await logPings(batch);
    } catch {
      this.buf.unshift(...batch); // 圏外なら次の機会に送る
    }
  }

  /**
   * 開発時だけの位置の上書き。URL に ?geo=35.9172,139.9098,10 を付けるか、
   * コンソールで __waggleGeo.set(lat, lon, acc) / __waggleGeo.clear()
   */
  private installDevOverride(): void {
    if (!config.devGeo) return;
    const apply = (lat: number, lon: number, acc = 10): void => {
      this.override = { lat, lon, acc, at: Date.now() };
      this.set(acc > POOR_ACC_M ? 'poor' : 'ok');
    };
    const q = new URLSearchParams(location.search).get('geo');
    if (q) {
      const [a, b, c] = q.split(',').map(Number);
      if (a !== undefined && b !== undefined && Number.isFinite(a) && Number.isFinite(b)) apply(a, b, c && Number.isFinite(c) ? c : 10);
    }
    // 上書き中も時刻を新しく保つ
    setInterval(() => {
      if (this.override) this.override = { ...this.override, at: Date.now() };
    }, 10_000);
    (window as unknown as Record<string, unknown>).__waggleGeo = {
      set: apply,
      clear: () => {
        this.override = null;
        this.set(this.last ? 'ok' : 'none');
      },
    };
  }
}

export const geo = new Geo();
